import { assumeInitialized } from "./base";

const phoneNumberRemovePattern = /[\s\(\)]/gm;
const ausPhoneNumberPattern = /^(?:\+61|0)?([0-9]{9})?$/;
const ausPhoneNumberValidationMessage = "Please enter a valid Australian phone number including area code";
const intlPhoneNumberPattern = /^(?:\+[0-9]+)?$/;
const intlPhoneNumberValidationMessage = "Please enter a valid international phone number (starting with + and a country code)";

export class RRRPhoneNumber extends HTMLElement {
  static tagName = "rrr-phone-number";
  static observedAttributes = ["australian"];

  countryPrefix: HTMLSpanElement;
  inputElement: HTMLInputElement = assumeInitialized();

  constructor() {
    super();

    this.countryPrefix = document.createElement("span");
    this.countryPrefix.textContent = "+61";

    this.addEventListener("change", () => this.validate());
  }

  get australian(): boolean {
    return this.getAttribute("australian") !== null;
  }

  connectedCallback() {
    this.inputElement = this.querySelector<HTMLInputElement>("input")!;
    this.updateCountry();
  }

  attributeChangedCallback() {
    this.updateCountry();
  }

  private updateCountry() {
    if (this.australian) {
      this.insertBefore(this.countryPrefix, this.firstChild!);
    } else {
      this.countryPrefix.remove();
    }

    this.validate();

    // notify rrr-field-with-validation above
    this.dispatchEvent(new Event("change", { bubbles: true }));
  }

  private validate() {
    const phoneNumber = this.inputElement.value.replaceAll(phoneNumberRemovePattern, "");

    const formatted = this.australian
      ? this.validateAustralianNumber(phoneNumber)
      : this.validateInternationalNumber(phoneNumber);

    if (formatted !== null) {
      this.inputElement.value = formatted;
      this.inputElement.setCustomValidity("");
    } else {
      this.inputElement.setCustomValidity(
        this.australian ? ausPhoneNumberValidationMessage : intlPhoneNumberValidationMessage
      );
    }
  }

  private validateAustralianNumber(input: string): string | null {
    const match = input.match(ausPhoneNumberPattern);

    if (!match) {
      // invalid
      return null;
    }

    const phone = match[1];

    if (!phone) {
      // empty
      return "";
    }

    if (phone[0] === "4") {
      // mobile number
      const group1 = phone.slice(0, 3);
      const group2 = phone.slice(3, 6);
      const group3 = phone.slice(6, 9);
      return `${group1} ${group2} ${group3}`;
    } else {
      const area = phone[0];
      const group1 = phone.slice(1, 5);
      const group2 = phone.slice(5, 9);
      return `(${area}) ${group1} ${group2}`;
    }
  }

  private validateInternationalNumber(input: string): string | null {
    const match = input.match(intlPhoneNumberPattern);

    if (match) {
      return input;
    } else {
      return null;
    }
  }
}
